import React, { useState, useEffect } from 'react';
import {
    Modal,
    Title,
    Text,
    Stack,
    Group,
    Button,
    ScrollArea,
    Select,
    Checkbox,
    Divider,
    Flex,
    Paper,
    Skeleton,
    Badge,
    Center
} from '@mantine/core';
import { Carousel } from '@mantine/carousel';
import { ImageData } from '../../types/Image';

export interface ExportSettings {
    nameFormat: string;
    dateFormat: string;
    includePeopleMetadata: boolean;
}

interface ExportSettingsModalProps {
    opened: boolean;
    onClose: () => void;
    onConfirm: (settings: ExportSettings, selectedImages: Set<string>) => void;  // Passing selected images
    selectedImages: Set<string>;
    events: Array<{ images: ImageData[] }>;
    unsorted: ImageData[];
}

export const ExportSettingsModal: React.FC<ExportSettingsModalProps> = ({
    opened,
    onClose,
    onConfirm,
    selectedImages,
    events,
    unsorted
}) => {
    const [nameFormat, setNameFormat] = useState<string>('original');
    const [dateFormat, setDateFormat] = useState<string>('yyyy-MM-dd');
    const [includePeopleMetadata, setIncludePeopleMetadata] = useState<boolean>(true);
    const [localSelectedImages, setLocalSelectedImages] = useState<Set<string>>(new Set(selectedImages));
    const [hoveredImage, setHoveredImage] = useState<string | null>(null);

    // Sync the local state with the selectedImages prop when the modal is opened
    useEffect(() => {
        setLocalSelectedImages(new Set(selectedImages));
    }, [opened, selectedImages]);

    // Function to handle the final export action
    const handleConfirm = () => {
        if (localSelectedImages.size === 0) {
            alert("Please select at least one image to export.");
            return;
        }

        // Update the parent component with the selected images
        onConfirm({
            nameFormat,
            dateFormat,
            includePeopleMetadata
        }, localSelectedImages); // Pass the modified selected images to parent
    };

    // Function to handle image toggle selection (check/uncheck)
    const toggleImageSelection = (imageKey: string) => {
        const newSelectedImages = new Set(localSelectedImages);

        if (newSelectedImages.has(imageKey)) {
            newSelectedImages.delete(imageKey); // Deselect the image
        } else {
            newSelectedImages.add(imageKey); // Select the image
        }

        setLocalSelectedImages(newSelectedImages); // Update the local selected images state
    };
    
    // Handle image click with proper event handling
    const handleImageClick = (imageKey: string, event: React.MouseEvent<Element, MouseEvent>) => {
        event.stopPropagation(); // Prevent triggering the export button when clicking on an image
        toggleImageSelection(imageKey);
    };

    // Combine and filter images from both events and unsorted
    const allImagesArray = [...events.flatMap((event) => event.images), ...unsorted]; // Flatten all images from events and include unsorted
    const selectedImagesArray = allImagesArray.filter((image) => selectedImages.has(image.key));
    
    // Separate images by source for display organization if needed
    const eventImages = events.flatMap((event) => event.images).filter(image => selectedImages.has(image.key));
    const unsortedSelectedImages = unsorted.filter(image => selectedImages.has(image.key));

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={<Title order={3}>Export Settings</Title>}
            size="xl"
            centered
        >
            <Stack gap="md">
                <Text mb="md">
                    Exporting {localSelectedImages.size} image{localSelectedImages.size !== 1 ? 's' : ''}:
                </Text>

                <Paper p="sm" mb="md">
                    
                    {selectedImagesArray.length > 0 ? (
                        <Carousel
                            height={280}
                            slideSize="28%"
                            slideGap="xs"
                            align="center"
                            slidesToScroll={1}
                            initialSlide={0}
                            withControls
                            // withIndicators
                            styles={{
                                control: {
                                    '&[data-inactive]': {
                                        opacity: 0,
                                        cursor: 'default',
                                    },
                                },
                            }}
                        >
                            {selectedImagesArray.map((image) => {
                                const isSelected = localSelectedImages.has(image.key);
                                const isFromEvent = eventImages.some(eventImage => eventImage.key === image.key);
                                
                                return (
                                    <Carousel.Slide key={image.key}>
                                        <div
                                            onMouseEnter={() => setHoveredImage(image.key)}
                                            onMouseLeave={() => setHoveredImage(null)}
                                            style={{
                                                position: 'relative',
                                                height: '100%',
                                                aspectRatio: '1 / 1',
                                                borderRadius: '4px',
                                                overflow: 'hidden',
                                                border: isSelected ? '2px solid #228be6' : '1px solid #e9ecef'
                                            }}
                                        >
                                            {image.thumbnail_url ? (
                                                <img
                                                    src={image.thumbnail_url}
                                                    alt={image.key}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover'
                                                    }}
                                                    onClick={(e) => handleImageClick(image.key, e)}
                                                />
                                            ) : (
                                                <Skeleton height="100%" width="100%" />
                                            )}
                                            

                                            
                                            {/* Checkbox for selecting image */}
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    top: '8px',
                                                    left: '8px',
                                                    zIndex: 2
                                                }}
                                            >
                                                <Checkbox
                                                    checked={isSelected}
                                                    onChange={() => toggleImageSelection(image.key)}
                                                    size="md"
                                                    radius="sm"
                                                    color="blue"
                                                />
                                            </div>
                                        </div>
                                    </Carousel.Slide>
                                );
                            })}
                        </Carousel>
                    ) : (
                        <Center p="xl">
                            <Text color="dimmed">No images selected for export</Text>
                        </Center>
                    )}
                </Paper>

                <Divider my="sm" />

                <Stack gap="md">
                    <Title order={4}>Export Options</Title>

                    <Select
                        label="File Name Format"
                        description="Choose how exported files will be named"
                        value={nameFormat}
                        onChange={(value) => setNameFormat(value || 'original')}
                        data={[
                            { value: 'original', label: 'Original filename' },
                            { value: 'date_original', label: 'Date + Original filename' },
                            { value: 'event_original', label: 'Event name + Original filename' }
                        ]}
                    />

                    <Select
                        label="Date Format"
                        description="Choose the date format for filenames and metadata"
                        value={dateFormat}
                        onChange={(value) => setDateFormat(value || 'yyyy-MM-dd')}
                        data={[
                            { value: 'yyyy-MM-dd', label: 'YYYY-MM-DD' },
                            { value: 'MM-dd-yyyy', label: 'MM-DD-YYYY' },
                            { value: 'dd-MM-yyyy', label: 'DD-MM-YYYY' }
                        ]}
                    />

                    <Checkbox
                        label="Include people metadata"
                        description="Include information about people identified in the photos"
                        checked={includePeopleMetadata}
                        onChange={(event) => setIncludePeopleMetadata(event.currentTarget.checked)}
                    />
                </Stack>

                <Group justify="space-between" mt="md">
                    <Button variant="outline" onClick={() => {
                        onClose();
                        setLocalSelectedImages(new Set(selectedImages)); // Reset local selected images to the parent state on cancel
                    }}>
                        Cancel
                    </Button>
                    <Button onClick={handleConfirm}>Export</Button>
                </Group>
            </Stack>
        </Modal>
    );
};

export default ExportSettingsModal;
